/**
 * sectionContractBase — SED sections whose payload is a typed CONTRACT even
 * when the section is `unavailable` (every value null, plus basis /
 * provenance / disclaimer keys that readers rely on).
 *
 * Why: the document-fallback path (`applyDocAwareConfidence` unavailable
 * branch, `sedDocResolver` tier 5) replaces `slot.data` WHOLESALE with the
 * extracted payload. For a plain provider section that is fine; for
 * `waterResource` it would destroy `waterResourceBasis`, `provenance` and
 * `disclaimer` — the honest-null contract — the first time a utility letter
 * fills the slot. This module owns the base payloads and the merge that
 * keeps them intact.
 */
import type { SedSectionId, WaterResourceEvidence, MunicipalWaterSupply, WastewaterService, WaterEvidenceProvenance } from '../../types/siteEvidence';

/** Canonical honest-unavailable disclaimer — the compile stub imports this, so the two can never drift. */
export const WATER_RESOURCE_DISCLAIMER =
  'Site-specific water resource data (consumptive-use allotment, water-rights ' +
  'status, aquifer depth-to-water, NPDES discharge permit) is NOT available ' +
  'from any clean point-queryable public API wired into this platform. Water ' +
  'rights and consumptive-use allotments are STATE-administered (e.g. TX TCEQ, ' +
  'CO DWR, CA eWRIMS); aquifer depth comes from USGS NWIS keyed by monitoring- ' +
  'well site id (needs a nearest-well resolver); NPDES permits are EPA ' +
  'ECHO/ICIS permit-id keyed (needs a point→facility join). These figures must ' +
  'be sourced via the relevant state water-rights DB / USGS NWIS / EPA ECHO — ' +
  'they are deliberately left unfilled rather than estimated. This section is ' +
  'distinct from the basin-level `waterStress` (WRI Aqueduct) section.';

/** Honest-empty WaterResourceEvidence — every value null, basis 'unavailable'. */
export function waterResourceContractBase(): WaterResourceEvidence {
  return {
    consumptiveUseAfy: null,
    waterRightsStatus: null,
    aquiferDepthToWaterFt: null,
    npdesPermitId: null,
    waterResourceBasis: 'unavailable',
    provenance: 'unavailable',
    disclaimer: WATER_RESOURCE_DISCLAIMER,
  };
}

/** Sections that carry a contract base. */
const CONTRACT_SECTIONS: ReadonlySet<SedSectionId> = new Set<SedSectionId>(['waterResource']);

export function sectionHasContractBase(sectionId: SedSectionId): boolean {
  return CONTRACT_SECTIONS.has(sectionId);
}

/**
 * Merge a document-extracted payload into a contract section without losing
 * the contract keys. For sections without a contract base the extracted
 * payload is returned unchanged (today's behaviour).
 *
 * Order: base ⊕ existing typed-null data ⊕ extracted; then the basis /
 * provenance flip to `'document'` because the slot is now document-filled.
 */
export function mergeIntoContractPayload(
  sectionId: SedSectionId,
  existing: Record<string, unknown> | null | undefined,
  extracted: Record<string, unknown>,
): Record<string, unknown> {
  if (sectionId !== 'waterResource') return extracted;
  const merged: Record<string, unknown> = {
    ...(waterResourceContractBase() as unknown as Record<string, unknown>),
    ...(existing ?? {}),
    ...extracted,
  };
  const existingBasis = (existing as { waterResourceBasis?: string } | null | undefined)?.waterResourceBasis;
  // A provider-grounded section keeps its basis; only the honest stub flips.
  if (existingBasis !== 'provider') {
    merged.waterResourceBasis = 'document';
    merged.provenance = 'document';
  }
  return merged;
}

/* ── Extraction payload normalisation ─────────────────────────────── */

const APPLIES_TO_VALUES = new Set(['this-site', 'other-project', 'unknown']);
const CONNECTION_TYPES = new Set(['gravity', 'force-main']);

function numberOrNull(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v.replace(/[,\s]/g, ''));
    return Number.isFinite(n) && v.trim() !== '' ? n : null;
  }
  return null;
}

function stringOrNull(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

function booleanOrNull(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const t = v.trim().toLowerCase();
    if (['true', 'yes', 'y'].includes(t)) return true;
    if (['false', 'no', 'n'].includes(t)) return false;
  }
  return null;
}

export interface ExtractionLineageForProvenance {
  documentId?: string;
  documentName?: string;
}

/** Attribution keys the extraction schema asks for on each sub-object. */
export function provenanceFromExtracted(
  raw: Record<string, unknown>,
  lineage?: ExtractionLineageForProvenance,
): WaterEvidenceProvenance | null {
  const appliesRaw = stringOrNull(raw.appliesToProject)?.toLowerCase().replace(/\s+/g, '-');
  const provenance: WaterEvidenceProvenance = {
    statedBy: stringOrNull(raw.statedBy),
    statedByRole: stringOrNull(raw.statedByRole),
    organisation: stringOrNull(raw.organisation),
    statedOn: stringOrNull(raw.statedOn),
    appliesToProject: appliesRaw && APPLIES_TO_VALUES.has(appliesRaw)
      ? (appliesRaw as WaterEvidenceProvenance['appliesToProject'])
      : 'unknown',
    ...(lineage?.documentId ? { documentId: lineage.documentId } : {}),
    ...(lineage?.documentName ? { documentName: lineage.documentName } : {}),
  };
  const hasAny = provenance.statedBy || provenance.statedByRole || provenance.organisation || provenance.statedOn
    || provenance.appliesToProject !== 'unknown' || provenance.documentId;
  return hasAny ? provenance : null;
}

function shapeSupply(raw: unknown, lineage?: ExtractionLineageForProvenance): MunicipalWaterSupply | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const out: MunicipalWaterSupply = {
    provider: stringOrNull(r.provider),
    sourceDescription: stringOrNull(r.sourceDescription),
    avgMgd: numberOrNull(r.avgMgd),
    peakMgd: numberOrNull(r.peakMgd),
    permittedMgd: numberOrNull(r.permittedMgd),
    excessMgd: numberOrNull(r.excessMgd),
    storageGallons: numberOrNull(r.storageGallons),
    nearestMainMiles: numberOrNull(r.nearestMainMiles),
    lineToSite: booleanOrNull(r.lineToSite),
    connectionType: null,
    notes: stringOrNull(r.notes),
    provenance: provenanceFromExtracted(r, lineage),
  };
  const ct = stringOrNull(r.connectionType)?.toLowerCase().replace(/\s+/g, '-');
  if (ct && CONNECTION_TYPES.has(ct)) out.connectionType = ct as MunicipalWaterSupply['connectionType'];
  return hasFigure(out) ? out : null;
}

function shapeWastewater(raw: unknown, lineage?: ExtractionLineageForProvenance): WastewaterService | null {
  const shaped = shapeSupply(raw, lineage);
  return shaped as WastewaterService | null;
}

/** True when at least one substantive field (not just provenance/notes) is present. */
function hasFigure(s: MunicipalWaterSupply): boolean {
  return s.provider !== null || s.avgMgd !== null || s.peakMgd !== null || s.permittedMgd !== null
    || s.excessMgd !== null || s.nearestMainMiles !== null || s.lineToSite !== null
    || s.connectionType !== null || (s.storageGallons ?? null) !== null || (s.sourceDescription ?? null) !== null;
}

/**
 * Shape a raw Gemini `waterResource` payload into the typed sub-objects.
 * Mutates `data` in place (the extraction pipeline works on the payload
 * object). Sub-objects without a substantive figure are removed so the
 * zero-fields rejection still fires for an empty extraction.
 */
export function normaliseWaterResourceExtraction(
  data: Record<string, unknown>,
  lineage?: ExtractionLineageForProvenance,
): void {
  const supply = shapeSupply(data.municipalSupply, lineage);
  const wastewater = shapeWastewater(data.wastewaterService, lineage);
  if (supply) data.municipalSupply = supply; else delete data.municipalSupply;
  if (wastewater) data.wastewaterService = wastewater; else delete data.wastewaterService;
  // Never let the model write the contract keys.
  delete data.waterResourceBasis;
  delete data.provenance;
  delete data.disclaimer;
}

/** Per-section payload normalisers run right after lineage projection. */
export function normaliseSectionPayload(
  sectionId: SedSectionId,
  data: Record<string, unknown>,
  lineage?: ExtractionLineageForProvenance,
): void {
  if (sectionId === 'waterResource') normaliseWaterResourceExtraction(data, lineage);
}
